import { ExperimentStatusKind, type Experiment } from '@gonfalon/experiments/types';
import type { FeatureFlag } from '@gonfalon/flags/types';
import { toFlagTargeting } from '@gonfalon/navigator/navigators';
import { Alert, Heading, Link } from '@launchpad-ui/components';

import styles from './ExperimentApprovalModal.module.css';

type ExperimentIterationStatus = NonNullable<Experiment['currentIteration']>['status'];

type ExperimentPendingApprovalAlertsProps = {
  flagData?: FeatureFlag;
  environmentKey?: string;
  flagKey?: string;
  projectKey?: string;
  hasPendingApproval?: boolean;
  experimentCurrentIterationStatus?: ExperimentIterationStatus;
};

export function ExperimentPendingApprovalAlerts({
  flagData,
  flagKey,
  environmentKey,
  projectKey,
  hasPendingApproval,
  experimentCurrentIterationStatus,
}: ExperimentPendingApprovalAlertsProps) {
  const flagTargetingLink =
    flagData && flagKey && environmentKey && projectKey
      ? {
          href: toFlagTargeting({ projectKey, flagKey, environmentKey }),
          label: flagData.name || flagKey,
        }
      : undefined;
  const isStopped = experimentCurrentIterationStatus === ExperimentStatusKind.STOPPED;

  if (hasPendingApproval && isStopped && flagTargetingLink) {
    return (
      <Alert status="warning">
        <Heading>Pending approval exists</Heading>
        <p>There’s already a pending approval, which may conflict with this change. We recommend you check:</p>
        <ul className={styles.alertList}>
          <li>Pending approvals in the right sidebar once you close this dialog</li>
          <li>
            If there were unwanted changes to the <Link href={flagTargetingLink.href}>{flagTargetingLink.label}</Link>{' '}
            flag before requesting this approval
          </li>
        </ul>
      </Alert>
    );
  }

  if (!hasPendingApproval && isStopped && flagTargetingLink) {
    return (
      <Alert status="info">
        <Heading>Potential flag changes</Heading>
        <p>
          Experiment might have stopped in order to make flag changes. Check if there were unwanted changes to the{' '}
          <Link href={flagTargetingLink.href}>{flagTargetingLink.label}</Link> flag before requesting this approval.
        </p>
      </Alert>
    );
  }

  if (hasPendingApproval) {
    return (
      <Alert status="warning">
        <Heading>Pending approval exists</Heading>
        <p>
          There’s already a pending approval, which may conflict with this change. Check pending approvals in the right
          sidebar once you close this dialog.
        </p>
      </Alert>
    );
  }

  return null;
}
